import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import { AiGoalFilterUserType, AiGoalStatus, AiGoalType, GoalSuggestion } from "../../DTO";
import { AppUserApi } from "../../api";
import { CustomBreadCrumb } from "../../components";
import { EditSuggestionsModal } from "../../components/goal-approval/EditSuggestionsModal";
import { useAppSelector } from "../../redux-config/hooks";
import { selectUser } from "../../redux-config/entities/user";
import { formatUserId } from "../../utils";
import "./style.scss";

const crumbs = [{ name: "", href: "" }, { name: "Dashboard", href: "/" }, { name: "Goals", href: "#" }];
const variants: Record<string, string> = { red: "danger", amber: "warning", green: "success" };
const labels: Record<string, string> = { priority: "Priority", support_habit: "Support habit", stability_habit: "Stability habit" };

const GoalContent: React.FC<{ goal: AiGoalType }> = ({ goal }) => (
    <div className="goal-review-content">
        <div className="goal-review-summary">
            <span className="goal-review-label">Goal</span>
            <strong>{goal.description || goal.recommendedAction || "No goal description"}</strong>
            {goal.reason && <p>{goal.reason}</p>}
        </div>
        {!!goal.suggestions?.length && <div className="goal-review-suggestions">
            {goal.suggestions.map((item: GoalSuggestion) => <div className="goal-review-suggestion" key={item.slot}>
                <Badge bg={variants[item.traffic_light]} text={item.traffic_light === "amber" ? "dark" : undefined}>{labels[item.slot]}</Badge>
                <span>{item.text}</span>
            </div>)}
        </div>}
        {(goal.focusArea || goal.metricTarget) && <div className="goal-review-metadata">
            {goal.focusArea && <span><b>Focus:</b> {goal.focusArea}</span>}
            {goal.metricTarget && <span><b>Target:</b> {goal.metricTarget}</span>}
        </div>}
    </div>
);

export const GoalApprovalsPage: React.FC = () => {
    const authUser = useAppSelector(selectUser);
    const [goals, setGoals] = useState<AiGoalType[]>([]);
    const [filterUsers, setFilterUsers] = useState<AiGoalFilterUserType[]>([]);
    const [goalType, setGoalType] = useState<"day" | "week">("day");
    const [status, setStatus] = useState<Extract<AiGoalStatus, "pending" | "approved">>("approved");
    const [selectedUserId, setSelectedUserId] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [editGoal, setEditGoal] = useState<AiGoalType | null>(null);

    const fetchGoals = useCallback((page = 1) => {
        setLoading(true);
        AppUserApi.GetGoals(authUser, { page, perPage: 20, status, type: goalType, userId: selectedUserId })
            .then((data) => {
                setGoals(data.goals); setFilterUsers(data.users); setTotal(data.total);
                setCurrentPage(data.currentPage); setLastPage(data.lastPage);
            })
            .catch((error) => Swal.fire({ icon: "error", text: String(error) }))
            .finally(() => setLoading(false));
    }, [authUser, goalType, selectedUserId, status]);

    useEffect(() => { fetchGoals(1); }, [fetchGoals]);

    const removeGoal = (goalId: number) => {
        setGoals((current) => current.filter((goal) => goal.id !== goalId));
        setTotal((count) => Math.max(0, count - 1));
    };

    const decide = (goalId: number, action: "approve" | "reject") => {
        Swal.fire({ title: `${action === "approve" ? "Approve" : "Reject"} this weekly goal?`, icon: action === "approve" ? "question" : "warning", showCancelButton: true, confirmButtonText: action === "approve" ? "Approve" : "Reject", confirmButtonColor: action === "approve" ? "#198754" : "#dc3545" }).then((result) => {
            if (!result.isConfirmed) return;
            setActionLoading(goalId);
            const request = action === "approve" ? AppUserApi.ApprovePendingGoal : AppUserApi.RejectPendingGoal;
            request(authUser, goalId).then(() => {
                removeGoal(goalId);
                Swal.fire({ icon: "success", title: action === "approve" ? "Approved" : "Rejected", timer: 1100, showConfirmButton: false });
            }).catch((error) => Swal.fire({ icon: "error", text: String(error) })).finally(() => setActionLoading(null));
        });
    };

    const selectedUser = filterUsers.find((item) => item.id === selectedUserId);
    return <>
        <Helmet><title>D2Type | Goal Management</title></Helmet>
        <div className="goal-review-page d-flex flex-column">
            <CustomBreadCrumb breadCrumbList={crumbs} />
            <div className="px-3 pb-4">
                <section className="goal-review-hero">
                    <div><span className="goal-review-eyebrow">Coach workspace</span><h2>Goal management</h2><p>Review weekly goals and view or adjust daily goals whenever support is needed.</p></div>
                    <div className="goal-review-total"><strong>{total}</strong><span>{status} {goalType} goals</span></div>
                </section>
                <Card className="goal-review-toolbar border-0"><Card.Body>
                    <div className="goal-review-tabs">
                        <button className={goalType === "day" ? "active" : ""} onClick={() => setGoalType("day")}>Daily goals <small>Auto-approved</small></button>
                        <button className={goalType === "week" ? "active" : ""} onClick={() => setGoalType("week")}>Weekly goals <small>Coach review</small></button>
                    </div>
                    <div className="goal-review-filters">
                        <div className="goal-review-status">
                            <button className={status === "pending" ? "active" : ""} onClick={() => setStatus("pending")}>Needs approval</button>
                            <button className={status === "approved" ? "active" : ""} onClick={() => setStatus("approved")}>Approved log</button>
                        </div>
                        <Form.Select value={selectedUserId ?? ""} onChange={(event) => setSelectedUserId(event.target.value ? Number(event.target.value) : undefined)}>
                            <option value="">All users</option>
                            {filterUsers.map((item) => <option value={item.id} key={item.id}>{item.displayId || formatUserId(item.id)} — {item.name || item.email} ({status === "pending" ? item.pendingCount : item.approvedCount})</option>)}
                        </Form.Select>
                        <Button variant="outline-secondary" onClick={() => fetchGoals(currentPage)} disabled={loading}>{loading ? <Spinner animation="border" size="sm" /> : "Refresh"}</Button>
                    </div>
                </Card.Body></Card>
                {selectedUser && <div className="goal-review-filter-note">Showing goals for <b>{selectedUser.displayId || formatUserId(selectedUser.id)}</b> · {selectedUser.name || selectedUser.email}</div>}
                {loading && goals.length === 0 ? <div className="goal-review-empty"><Spinner animation="border" /><span>Loading goals…</span></div> : goals.length === 0 ? <div className="goal-review-empty"><span className="goal-review-empty-icon">✓</span><h5>No matching goals</h5><p>Try a different period, status, or user.</p></div> : <div className="goal-review-list">
                    {goals.map((goal) => <article className="goal-review-card" key={goal.id}>
                        <header>
                            <div className="goal-review-person"><span className="goal-review-avatar">{(goal.user?.name || goal.user?.email || "U").charAt(0).toUpperCase()}</span><div><Link to={`/app-user/info/${goal.userId}`}>{goal.user?.name || goal.user?.email || `User ${formatUserId(goal.userId)}`}</Link><small>{formatUserId(goal.userId)}</small></div></div>
                            <div className="goal-review-badges"><Badge bg={goal.status === "pending" ? "warning" : "success"} text={goal.status === "pending" ? "dark" : undefined}>{goal.status === "pending" ? "Needs approval" : "Approved"}</Badge><Badge bg="light" text="dark">{goal.type === "week" ? `Week ${goal.weekId ?? "—"}` : `Day ${goal.dayId ?? "—"}`}</Badge><Badge bg={goal.aiUsed ? "info" : "secondary"}>{goal.aiUsed ? "AI assisted" : "Fallback"}</Badge></div>
                        </header>
                        <GoalContent goal={goal} />
                        <footer><small>{goal.createdAt?.slice(0, 10) || "Date unavailable"}</small><div><Button size="sm" variant="outline-primary" onClick={() => setEditGoal(goal)}>Edit goal</Button>{goal.type === "week" && goal.status === "pending" && <><Button size="sm" variant="success" disabled={actionLoading === goal.id} onClick={() => decide(goal.id, "approve")}>Approve</Button><Button size="sm" variant="outline-danger" disabled={actionLoading === goal.id} onClick={() => decide(goal.id, "reject")}>Reject</Button></>}</div></footer>
                    </article>)}
                </div>}
                {goals.length > 0 && <div className="goal-review-pagination"><small>Page {currentPage} of {lastPage} · {total} results</small><div><Button size="sm" variant="outline-secondary" disabled={currentPage <= 1 || loading} onClick={() => fetchGoals(currentPage - 1)}>Previous</Button><Button size="sm" variant="outline-secondary" disabled={currentPage >= lastPage || loading} onClick={() => fetchGoals(currentPage + 1)}>Next</Button></div></div>}
            </div>
        </div>
        {editGoal && <EditSuggestionsModal goal={editGoal} show onHide={() => setEditGoal(null)} onSaved={(updated) => setGoals((current) => current.map((goal) => goal.id === updated.id ? updated : goal))} onSavedAndApproved={removeGoal} />}
    </>;
};
